import type { ConnectedPosition } from '@angular/cdk/overlay';
import {
  Directive,
  ElementRef,
  ViewContainerRef,
  inject,
  input,
} from '@angular/core';
import { isFunction } from 'lodash';
import type {
  OnyxTooltipContext,
  OnyxTooltipTemplateContext,
} from '../components/tooltip/interfaces';
import { ONYX_TOOLTIP_DELAY } from '../constants';
import type { OnyxOverlayPosition } from '../enums';
import type { OnyxOptional } from '../interfaces';
import type {
  OnyxTooltipColor,
  OnyxTooltipConfig,
} from '../internal/components/onyx-tooltip-overlay/onyx-tooltip-overlay.component';
import { OnyxTooltipService } from '../internal/services/onyx-tooltip.service';

@Directive({
  selector: '[onyxTooltip]',
  host: {
    '(mouseenter)': `show()`,
    '(focus)': `show()`,
    '(click)': `close()`,
    '(keydown.shift.tab)': `close()`,
    '(keydown.tab)': `close()`,
  },
})
export class OnyxTooltipDirective {
  private readonly tooltipService = inject(OnyxTooltipService);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly viewContainerRef = inject(ViewContainerRef);

  public readonly onyxTooltipContext = input.required<
    OnyxOptional<OnyxTooltipContext>
  >({
    alias: 'onyxTooltip',
  });
  public readonly onyxTooltipTemplateContext =
    input<OnyxTooltipTemplateContext>(null);
  public readonly onyxTooltipColor = input<OnyxTooltipColor>('black');
  public readonly onyxTooltipShowArrow = input(true);
  public readonly onyxTooltipTextAlign =
    input<OnyxTooltipConfig['textAlign']>('center');
  public readonly onyxTooltipArrowOffsets = input<{
    x?: number;
    y?: number;
    calculateY?: boolean;
    calculateX?: boolean;
  }>({});
  public readonly onyxTooltipPositions = input<OnyxOverlayPosition[]>([]);
  public readonly onyxTooltipTargetRef = input<HTMLElement>();
  public readonly onyxTooltipDelay = input(ONYX_TOOLTIP_DELAY);
  public readonly onyxTooltipTheme = input<string[]>([]);
  public readonly onyxTooltipOverridePositions =
    input<Partial<ConnectedPosition>>();
  public readonly onyxTooltipDisabled = input<boolean | (() => boolean)>(false);

  public show(): void {
    const context = this.onyxTooltipContext();
    const disabled = this.onyxTooltipDisabled();
    if (!context || (isFunction(disabled) ? disabled() : disabled)) return;

    this.tooltipService.attachTooltip(
      this.elementRef,
      this.viewContainerRef,
      context,
      this.onyxTooltipShowArrow(),
      this.onyxTooltipTextAlign(),
      this.onyxTooltipArrowOffsets(),
      this.onyxTooltipPositions(),
      this.onyxTooltipTargetRef(),
      this.onyxTooltipDelay(),
      this.onyxTooltipOverridePositions(),
      this.onyxTooltipColor(),
      this.onyxTooltipTemplateContext(),
    );
  }

  public close(): void {
    this.tooltipService.detachTooltip();
  }
}
